/**
 * AI Learning & Insights — a running compilation of everything the AI is
 * learning and using for decision-making: current learned state (tiers,
 * defunds, weights, gates, culls), the learner-decision feed with reasons,
 * today's War Room committee intelligence, realized-outcome insight slices,
 * accumulated learnings/principles, and shadow-soak evidence.
 *
 * Read-only reporting surface over GET /api/reporting/ai-learning.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  BookOpen,
  Brain,
  FlaskConical,
  GraduationCap,
  Loader2,
  RefreshCw,
  Scale,
  Shield,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { reportingApi } from '../lib/api';
import type {
  AiLearningDecision,
  AiLearningResponse,
  AiLearningSlice,
} from '../types/titan';

const fmt$ = (v: number) =>
  `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
const pnlClass = (v: number) =>
  v > 0 ? 'text-emerald-500' : v < 0 ? 'text-rose-500' : 'text-muted-foreground';
const fmtTs = (ts: string | null) =>
  ts ? new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const KIND_META: Record<AiLearningDecision['kind'], { label: string; cls: string }> = {
  tier: { label: 'Tier move', cls: 'bg-blue-500/10 text-blue-500' },
  tier_family: { label: 'Family promote', cls: 'bg-blue-500/10 text-blue-500' },
  bleeder_retire: { label: 'Bleeder retired', cls: 'bg-rose-500/10 text-rose-500' },
  defund: { label: 'Defund', cls: 'bg-amber-500/10 text-amber-600' },
  rollback: { label: 'Rollback', cls: 'bg-purple-500/10 text-purple-500' },
};

function KindChip({ kind }: { kind: AiLearningDecision['kind'] }) {
  const meta = KIND_META[kind] ?? { label: kind, cls: 'bg-muted text-muted-foreground' };
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function SliceTable({ title, data }: { title: string; data?: Record<string, AiLearningSlice> }) {
  const rows = Object.entries(data ?? {}).sort((a, b) => b[1].pnl - a[1].pnl);
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">{title}</div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No data yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="h-8 text-xs">Bucket</TableHead>
                <TableHead className="h-8 text-xs text-right">N</TableHead>
                <TableHead className="h-8 text-xs text-right">P&L</TableHead>
                <TableHead className="h-8 text-xs text-right">Win %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(([key, s]) => (
                <TableRow key={key}>
                  <TableCell className="py-1.5 text-xs font-medium">{key}</TableCell>
                  <TableCell className="py-1.5 text-xs text-right">{s.n}</TableCell>
                  <TableCell className={`py-1.5 text-xs text-right font-medium ${pnlClass(s.pnl)}`}>{fmt$(s.pnl)}</TableCell>
                  <TableCell className="py-1.5 text-xs text-right">{s.win_rate.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

const AiLearning: React.FC = () => {
  const [data, setData] = useState<AiLearningResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportingApi.aiLearning(30);
      setData(res);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load AI learning data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  if (!data && loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const live = data?.learned_state.live;
  const guard = (live?.guard ?? {}) as Record<string, unknown>;
  const gate = typeof guard.min_confidence_to_gate === 'number' ? guard.min_confidence_to_gate : null;
  const tiers = data?.learned_state.tiers ?? {};
  const tierTotal = Object.values(tiers).reduce((a, b) => a + b, 0);
  const defundCount = (live?.defunded_sessions?.length ?? 0) + (live?.defunded_regimes?.length ?? 0);
  const strategy = data?.war_room.strategy ?? null;
  const feed = data?.decision_feed ?? [];
  const dials = data?.shadow_soak.dials ?? [];

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-foreground flex items-center gap-2">
            <GraduationCap className="w-8 h-8 text-primary" />
            AI Learning & Insights
          </h1>
          <p className="text-muted-foreground mt-1">
            What the AI is learning and using to decide — learned state, decisions with reasons, and the trends the data is teaching.
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={load} title="Refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stat row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider font-semibold">Confidence gate</CardDescription>
            <CardTitle className="text-3xl">{gate !== null ? gate : '—'}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-xs text-muted-foreground">
            {live?.available ? 'Live (AI-tuned)' : 'Controller offline'}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider font-semibold">Active fleet</CardDescription>
            <CardTitle className="text-3xl">{tierTotal}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-xs text-muted-foreground">
            {Object.entries(tiers).map(([t, n]) => `${n} ${t}`).join(' · ') || 'no active patterns'}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider font-semibold">Defunded slices</CardDescription>
            <CardTitle className="text-3xl">{live?.available ? defundCount : '—'}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-xs text-muted-foreground">
            {live?.available
              ? [...(live?.defunded_sessions ?? []), ...(live?.defunded_regimes ?? [])].join(', ') || 'none defunded'
              : 'requires running controller'}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider font-semibold">Learner decisions (30d)</CardDescription>
            <CardTitle className="text-3xl">{feed.length}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-xs text-muted-foreground">
            tier moves · defunds · retirements · rollbacks
          </CardContent>
        </Card>
      </div>

      {/* War Room intelligence */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="w-4 h-4 text-primary" /> Today&apos;s War Room intelligence
          </CardTitle>
          <CardDescription>
            {strategy
              ? `${strategy.date ?? ''} — ${strategy.bot_count} bot(s) deployed · confidence ${strategy.market_confidence_score ?? '—'} · risk ${strategy.risk_level ?? '—'}`
              : 'No War Room strategy recorded yet.'}
          </CardDescription>
        </CardHeader>
        {strategy && (
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                strategy.overall_bias === 'bullish' ? 'bg-emerald-500/10 text-emerald-600'
                  : strategy.overall_bias === 'bearish' ? 'bg-rose-500/10 text-rose-500'
                    : 'bg-muted text-muted-foreground'}`}>
                {strategy.overall_bias ?? 'neutral'}
              </span>
              {strategy.key_themes.map(t => (
                <span key={t} className="inline-block rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">{t}</span>
              ))}
              {strategy.regime_tags.map(t => (
                <span key={t} className="inline-block rounded bg-blue-500/10 px-2 py-0.5 text-xs text-blue-500">{t}</span>
              ))}
            </div>
            {strategy.rationale && <p className="text-sm text-foreground">{strategy.rationale}</p>}
            {strategy.no_trade_day && (
              <p className="text-sm text-amber-600">No-trade day: {strategy.no_trade_reason || 'AI stood down.'}</p>
            )}
            {strategy.strategy_decisions.length > 0 && (
              <div className="text-xs text-muted-foreground">
                {strategy.strategy_decisions.length} strategy decision(s), of which{' '}
                {strategy.strategy_decisions.filter(d => (d.drew_from_learning_ids?.length ?? 0) > 0).length} drew on stored learnings.
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Decision feed + learned state */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" /> Recent AI decisions
            </CardTitle>
            <CardDescription>Every learner action with its stated reason, newest first.</CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-auto max-h-[420px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">When</TableHead>
                  <TableHead className="text-xs">Kind</TableHead>
                  <TableHead className="text-xs">Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feed.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-6 text-muted-foreground text-sm">
                      No learner decisions in the window yet — they appear here as the sweeps act.
                    </TableCell>
                  </TableRow>
                ) : feed.map((d, i) => (
                  <TableRow key={`${d.ts}-${i}`}>
                    <TableCell className="py-1.5 text-xs whitespace-nowrap text-muted-foreground">{fmtTs(d.ts)}</TableCell>
                    <TableCell className="py-1.5"><KindChip kind={d.kind} /></TableCell>
                    <TableCell className="py-1.5 text-xs">{d.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Scale className="w-4 h-4 text-primary" /> Learned state now
            </CardTitle>
            <CardDescription>What the AI currently believes and enforces.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <span className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Auto-retired bleeders</span>
              {data?.learned_state.auto_retired_bleeders.length ? (
                <ul className="mt-1 space-y-0.5">
                  {data.learned_state.auto_retired_bleeders.map((b, i) => (
                    <li key={i} className="text-rose-500 text-xs">{b.name ?? 'unnamed'} <span className="text-muted-foreground">({fmtTs(b.archived_at)})</span></li>
                  ))}
                </ul>
              ) : <p className="text-xs text-muted-foreground mt-1">None yet.</p>}
            </div>
            {live?.available && (
              <>
                <div>
                  <span className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Strategy weights (learned)</span>
                  <p className="text-xs mt-1">
                    {Object.keys(live.strategy_weights ?? {}).length
                      ? `${Object.keys(live.strategy_weights ?? {}).length} pattern(s) weighted · ${Object.values(live.strategy_weights ?? {}).filter(w => w < 1).length} trimmed · ${Object.values(live.strategy_weights ?? {}).filter(w => w > 1).length} earned-up`
                      : 'No weights computed yet today.'}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Symbol blocklist</span>
                  <p className="text-xs mt-1">{live.symbol_blocklist?.length ? live.symbol_blocklist.join(', ') : 'empty'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Proven families (full-size entry)</span>
                  <p className="text-xs mt-1">{live.proven_family_templates?.length ? live.proven_family_templates.join(', ') : 'none proven yet'}</p>
                </div>
                {(live.rollback_pending?.length ?? 0) > 0 && (
                  <div>
                    <span className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Rollback watches</span>
                    <ul className="mt-1 space-y-0.5">
                      {live.rollback_pending!.map((r, i) => (
                        <li key={i} className="text-xs">{r.param_path} on {r.pattern_id.slice(0, 8)}… ({String(r.incumbent_value)} → {String(r.applied_value)})</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            <div>
              <span className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Entry dials</span>
              <p className="text-xs mt-1">
                {Object.entries(data?.learned_state.dial_settings ?? {}).map(([k, v]) => `${k}=${v}`).join(' · ') || '—'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insight slices */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" /> What the data is teaching
          </CardTitle>
          <CardDescription>
            Realized-outcome slices over the last {data?.insights.window_days ?? 30} days ({data?.insights.n ?? 0} trades) — the evidence the learners act on.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <SliceTable title="By AI confidence" data={data?.insights.by_confidence} />
            <SliceTable title="By session" data={data?.insights.by_session} />
            <SliceTable title="By regime" data={data?.insights.by_regime} />
            <SliceTable title="By exit reason" data={data?.insights.by_exit_reason} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-500" /> Best patterns
              </div>
              {(data?.insights.best_patterns ?? []).map(p => (
                <div key={p.name} className="flex justify-between text-xs py-1 border-b border-border last:border-0">
                  <span>{p.name} <span className="text-muted-foreground">({p.n} trades, {p.win_rate.toFixed(0)}% WR)</span></span>
                  <span className={pnlClass(p.pnl)}>{fmt$(p.pnl)}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
                <TrendingDown className="w-3.5 h-3.5 text-rose-500" /> Worst patterns
              </div>
              {(data?.insights.worst_patterns ?? []).map(p => (
                <div key={p.name} className="flex justify-between text-xs py-1 border-b border-border last:border-0">
                  <span>{p.name} <span className="text-muted-foreground">({p.n} trades, {p.win_rate.toFixed(0)}% WR)</span></span>
                  <span className={pnlClass(p.pnl)}>{fmt$(p.pnl)}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Learnings + shadow evidence */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" /> Accumulated learnings
            </CardTitle>
            <CardDescription>Structured lessons the War Room writes nightly and draws on.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 overflow-auto max-h-[420px]">
            {(data?.war_room.learnings ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No learnings recorded yet.</p>
            ) : data!.war_room.learnings.map((l, i) => (
              <div key={l.id ?? i} className="rounded border border-border p-2">
                <div className="flex items-center gap-1.5 mb-1">
                  {l.severity && (
                    <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      l.severity === 'high' ? 'bg-rose-500/10 text-rose-500'
                        : l.severity === 'medium' ? 'bg-amber-500/10 text-amber-600'
                          : 'bg-muted text-muted-foreground'}`}>{l.severity}</span>
                  )}
                  {l.category && <span className="text-[10px] text-muted-foreground uppercase">{l.category}</span>}
                  {l.regime_tags.map(t => (
                    <span key={t} className="inline-block rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] text-blue-500">{t}</span>
                  ))}
                </div>
                <p className="text-xs text-foreground">{l.text}</p>
              </div>
            ))}
            {(data?.war_room.principles ?? []).map((p, i) => (
              <div key={i} className="rounded border border-primary/30 bg-primary/5 p-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-primary mb-1">
                  Monthly principles {p.month ? `— ${p.month}` : ''}
                </div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {(p.principles ?? []).map((line, j) => <li key={j} className="text-xs">{line}</li>)}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-primary" /> Shadow evidence & experiments
            </CardTitle>
            <CardDescription>What the platform is about to learn enough to act on.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                Shadow dials (7d would-act volume)
              </div>
              {dials.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No shadow events in the window.</p>
              ) : (
                <div className="space-y-1">
                  {dials.map(d => (
                    <div key={d.dial} className="flex justify-between text-xs py-0.5">
                      <span className="font-mono">{d.dial}</span>
                      <span className="text-muted-foreground">{d.total} event(s)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5 flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" /> Hermes actions
              </div>
              {(data?.hermes_actions ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No Hermes actions recorded.</p>
              ) : data!.hermes_actions.map((a, i) => (
                <div key={i} className="text-xs py-0.5">
                  <span className="text-muted-foreground">{fmtTs(a.ts)}</span>{' '}
                  <span className="font-medium">{a.action_type}</span>
                  {a.pattern_name ? ` — ${a.pattern_name}` : ''}
                  {a.dry_run ? <span className="text-amber-600"> (dry-run)</span> : null}
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                Bayesian tuning activity
              </div>
              {(data?.tuning ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No tuning observations yet.</p>
              ) : data!.tuning.slice(0, 8).map((t, i) => (
                <div key={i} className="flex justify-between text-xs py-0.5">
                  <span className="font-mono">{t.param_path} <span className="text-muted-foreground">({t.pattern_id.slice(0, 8)}…)</span></span>
                  <span className="text-muted-foreground">{t.observations} obs</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-muted-foreground text-right">
        Updated {data ? fmtTs(data.generated_at) : '—'} · refreshes every 60s
      </p>
    </div>
  );
};

export default AiLearning;
